// types/Personnel.ts - ENHANCED WITH IMC LEAVE SYSTEM

export enum PersonnelRole {
  SoundEngineer = 'Sound Engineer',
  LightingEngineer = 'Lighting Engineer',
  VisionMixEngineer = 'Vision Mix Engineer',
  StageManager = 'Stage Manager',
  foh = 'Front of House',
  ProductionManager = 'Production Manager',
  Volunteer = 'Volunteer',
  Supplier = 'Supplier',
  BacklineSupplier = 'Backline Supplier',
  StageHand = 'Stage Hand',
  ArtistLiaison = 'Artist Liaison',
  PhotographerVideographer = 'Photographer/Videographer',
  Runner = 'Runner',
  StructuralEngineer = 'Structural Engineer',
  Staff = 'Staff',
  Other = 'Other'
}

export enum SkillLevel {
  Beginner = 'Beginner',
  Intermediate = 'Intermediate',
  Advanced = 'Advanced',
  Expert = 'Expert'
}

export enum EmploymentStatus {
  Employee = 'employee',
  Contractor = 'contractor',
  Freelancer = 'freelancer',
  Volunteer = 'volunteer',
  External = 'external'
}

export enum EmploymentType {
  FullTime = 'full-time',
  PartTime = 'part-time',
  Contractor = 'contractor'
}

// ⭐ NEW: Leave entitlement tracking per IMC policy
export interface LeaveEntitlement {
  // Annual Leave (AL) - IMC Policy: 20 days default, admin adjustable
  annualLeaveTotal: number;           // Total AL days for current year (20-21 typically)
  annualLeaveCurrentYear: number;     // AL days for the current year
  annualLeaveUsed: number;            // AL days used this year
  annualLeaveYear: number;            // Which year this entitlement is for
  
  // Time in Lieu (TIL) - IMC Guideline: Ideally not beyond 2 working days (16 hours)
  timeInLieuHours: number;            // TIL hours accumulated (guideline: ~16 hours)
  timeInLieuUsed: number;             // TIL hours used
  timeInLieuEntries: Array<{          // Track individual TIL entries for 28-day guideline
    hours: number;
    earnedDate: Date;
    expiryDate: Date;                 // earnedDate + 28 days (guideline)
    description: string;
    rotaEntryId?: string;
  }>;
  
  // Christmas mandatory leave tracking (part of AL entitlement)
  christmasLeaveUsed: number;         // Days used for mandatory Christmas closure (~5 days)
  
  // Admin tracking
  lastModified: Date;
  modifiedBy?: string;                // Track who last changed AL entitlement
  
  // Holiday year tracking (Jan 1 - Dec 31)
  yearStartDate: Date;                // Usually Jan 1
  yearEndDate: Date;                  // Usually Dec 31
}

// ⭐ NEW: Leave transaction for audit trail
export interface LeaveTransaction {
  _id?: string;
  personnelId: string;
  date: Date;
  leaveType: 'annual-leave' | 'time-lieu' | 'christmas-leave';
  action: 'earned' | 'used' | 'adjusted' | 'forfeited';
  amount: number;                     // Hours for TIL, days for AL
  balance: number;                    // Balance after transaction
  description: string;                // e.g., "Annual leave taken", "TIL earned from weekend event"
  rotaEntryId?: string;              // Link to rota entry if applicable
  approvedBy?: string;
  createdAt: Date;
}

export interface PersonnelFormData {
  _id?: string;
  id?: string;
  name: string;
  email: string;
  phone: string;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  };
  roles: PersonnelRole[];
  employmentStatus: EmploymentStatus;  // <- ADD THIS LINE
  employeeNumber?: string;     
  skillLevel: SkillLevel;
  specialization?: string;
  fee: number;
  availability: {
    from: Date;
    to: Date;
  };
  equipment?: string[];
  certifications?: string[];
  experience: number;
  bio?: string;
  profileImage?: string;
  socialMedia?: {
    linkedin?: string;
    twitter?: string;
    instagram?: string;
  };
  rating?: number;
  reviews?: Array<{
    reviewerId: string;
    comment: string;
    rating: number;
    date: Date;
  }>;
  performances?: string[];

  // ⭐ NEW: Staff employment details
  employmentType?: EmploymentType;
  startDate?: Date;                   // Employment start date for pro-rata calculations
  
  // ⭐ NEW: Leave entitlement (only for Staff role)
  leaveEntitlement?: LeaveEntitlement;
}

export interface Personnel extends PersonnelFormData {
  _id: string;
  createdAt: Date;
  updatedAt: Date;
  
  // ⭐ NEW: Computed properties
  isStaff?: boolean;                  // Virtual: roles.includes('Staff')
  currentLeaveBalance?: {
    annualLeave: number;              // Remaining AL days
    timeInLieu: number;               // Available TIL hours
    timeInLieuDays: number;           // TIL converted to days (8 hours = 1 day)
    totalAvailableDays: number;       // AL + TIL days combined
  };
}

// ⭐ NEW: Leave calculation helpers
export interface LeaveCalculation {
  // Annual Leave
  totalAnnualLeave: number;
  usedAnnualLeave: number;
  remainingAnnualLeave: number;
  christmasLeaveAllocated: number;
  availableForBooking: number;        // Remaining minus Christmas allocation
  
  // Time in Lieu  
  tilHours: number;
  tilDays: number;                    // TIL hours / 8
  tilMaxHours: number;                // 16 hours max per IMC policy
  tilAvailableHours: number;          // Max - current
  
  // Combined
  totalDaysAvailable: number;
  
  // Warnings
  nearingTilLimit: boolean;           // Close to 16 hour limit
  nearingForfeiture: boolean;         // Close to Jan 31 deadline
}

// ⭐ NEW: Validation result (warnings, not blocking errors)
export interface LeaveValidation {
  isValid: boolean;                   // Always true unless critical system error
  canProceed: boolean;                // Always true - admin can override anything
  warnings: string[];                // Guidelines being exceeded
  suggestions: string[];              // Helpful suggestions
  
  // Guideline checks (informational only)
  tilApproachingGuideline: boolean;   // Near 16 hour guideline
  tilExceedsGuideline: boolean;       // Over 16 hour guideline
  tilNearingExpiry: boolean;          // Some TIL approaching 28 days
  tilExpired: boolean;                // Some TIL past 28 days (guideline)
  
  // Business context (informational)
  withinExclusionPeriod: boolean;     // Festival periods etc
  minimumStaffCoverage: boolean;      // 2 staff guideline
  advanceNoticeMet: boolean;          // 4 weeks AL / 2 weeks TIL guideline
}

// ⭐ NEW: Action result with warnings but no blocking
export interface LeaveActionResult {
  success: boolean;                   // Operation completed
  warnings: string[];                // Guidelines exceeded
  suggestions: string[];              // Helpful next steps
  appliedOverride?: boolean;          // Admin override was used
}

// ⭐ NEW: IMC-specific leave types for Rota integration
export enum IMCLeaveType {
  AnnualLeave = 'annual-leave',       // Deduct from AL entitlement
  TimeInLieu = 'time-lieu',           // Deduct from TIL hours
  SickLeave = 'sick-leave',           // No deduction, statutory
  ChristmasLeave = 'christmas-leave'  // Part of mandatory closure
}

// ⭐ NEW: Holiday exclusion periods per IMC policy
export interface ExclusionPeriod {
  name: string;
  startDate: Date;
  endDate: Date;
  reason: string;
  isRecurring: boolean;               // Annual events like festivals
}

export const IMC_EXCLUSION_PERIODS = {
  ARTS_COUNCIL: 'Arts Council Application Period',
  SPECTRUM: 'Spectrum Festival',
  TWELVE_POINTS: '12 Points Festival', 
  HOTTER_THAN_JULY: 'Hotter Than July Festival',
  CHRISTMAS: 'Christmas Closure Period'
} as const;